import type { ToolbarActionConfig } from '@/blocks/object-toolbar';

/**
 * Inputs for {@link buildEnterpriseRiskActions}. Each `can*`/`can*Loading` pair
 * mirrors the page's `useHasPermissionQuery` results. These actions target a
 * *different* object type than the risk detail toolbar's `parentObject` (the
 * risk), so they are gated here at build time rather than via the toolbar's
 * per-action `permission` field — the resolver keys instance-level permissions
 * to `parentObject`'s type and would mis-resolve a foreign `objectType`
 * (CUS-152). Extracted as a pure helper so the gating is unit-testable in
 * isolation (deep-review follow-up).
 */
export interface EnterpriseRiskActionsArgs {
  /** `enterprise_risk` module enabled. When false, no actions are produced. */
  enabled: boolean;
  canLinkToEnterpriseRisk: boolean;
  canLinkToEnterpriseRiskLoading: boolean;
  canLinkToEntity: boolean;
  canLinkToEntityLoading: boolean;
  /** The risk already has an enterprise-risk/entity link (drives the remove action). */
  hasEnterpriseRiskLink: boolean;
  canUnlinkEnterpriseRisk: boolean;
  canUnlinkEnterpriseRiskLoading: boolean;
  /**
   * true → the link is to an enterprise risk (remove label = "Remove from
   * enterprise risk"); false → to an entity (remove label = "Remove from entity").
   */
  isLinkedToEnterpriseRisk: boolean;
  labels: {
    addToEnterpriseRisk: string;
    addToEntity: string;
    removeFromEnterpriseRisk: string;
    removeFromEntity: string;
  };
  handlers: {
    onAddToEnterpriseRisk: () => void;
    onAddToEntity: () => void;
    onRemove: () => void;
  };
}

/**
 * Build the risk detail toolbar's "Enterprise risk" overflow actions, gated at
 * build time by the caller's permission booleans (see the note on
 * {@link EnterpriseRiskActionsArgs}). Returns `[]` when the module is disabled or
 * no action is permitted — the caller drops the overflow group when empty.
 */
export const buildEnterpriseRiskActions = ({
  enabled,
  canLinkToEnterpriseRisk,
  canLinkToEnterpriseRiskLoading,
  canLinkToEntity,
  canLinkToEntityLoading,
  hasEnterpriseRiskLink,
  canUnlinkEnterpriseRisk,
  canUnlinkEnterpriseRiskLoading,
  isLinkedToEnterpriseRisk,
  labels,
  handlers,
}: EnterpriseRiskActionsArgs): ToolbarActionConfig[] => {
  if (!enabled) {
    return [];
  }

  return [
    ...(canLinkToEnterpriseRisk && !canLinkToEnterpriseRiskLoading
      ? [
          {
            id: 'add-to-enterprise-risk',
            label: labels.addToEnterpriseRisk,
            iconName: 'link-external-01' as const,
            onClick: handlers.onAddToEnterpriseRisk,
          },
        ]
      : []),
    ...(canLinkToEntity && !canLinkToEntityLoading
      ? [
          {
            id: 'add-to-entity',
            label: labels.addToEntity,
            iconName: 'link-external-01' as const,
            onClick: handlers.onAddToEntity,
          },
        ]
      : []),
    ...(hasEnterpriseRiskLink &&
    canUnlinkEnterpriseRisk &&
    !canUnlinkEnterpriseRiskLoading
      ? [
          {
            id: 'remove-from-enterprise-risk',
            label: isLinkedToEnterpriseRisk
              ? labels.removeFromEnterpriseRisk
              : labels.removeFromEntity,
            iconName: 'link-external-01' as const,
            onClick: handlers.onRemove,
          },
        ]
      : []),
  ];
};
